import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { isImportantDay, getImportantDayInfo, getImportantDayColor } from '../utils/importantDays';

const MobileCalendar = ({ dob, currentDate, selectedISO, onPick, isDateAllowed, rangeSummary, onPrevMonth, onNextMonth, canPrevMonth, canNextMonth }) => {
  const [modalDay, setModalDay] = useState(null);
  
  // Función para construir la matriz del mes
  
  const buildMonthMatrix = (baseDate) => {
    const Y = baseDate.getFullYear();
    const M = baseDate.getMonth();
    const first = new Date(Y, M, 1);
    // Ajustar para que Lunes sea 0, Domingo sea 6
    let startDow = first.getDay() - 1;
    if (startDow < 0) startDow = 6; // Si es Domingo, convertirlo a 6
    
    const daysInMonth = new Date(Y, M + 1, 0).getDate();
    const prevMonthDays = new Date(Y, M, 0).getDate();
    const cells = [];
    
    for (let i = 0; i < startDow; i++) {
      const day = prevMonthDays - startDow + 1 + i;
      cells.push({ date: new Date(Y, M - 1, day), inMonth: false });
    }
    
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(Y, M, d), inMonth: true });
    }
    
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      const next = new Date(last);
      next.setDate(last.getDate() + 1);
      cells.push({ date: next, inMonth: next.getMonth() === M });
    }
    
    return cells;
  };

  // Funciones de numerología (completas)
  const sumDigits = (n) => Math.abs(n).toString().split("").reduce((a, b) => a + Number(b), 0);
  const reduceNum = (n) => {
    let x = Math.abs(n);
    while (x >= 10 && x !== 11 && x !== 22) x = sumDigits(x);
    return x;
  };
  const universalYear = (Y) => reduceNum(Y);
  const universalMonth = (M, Y) => reduceNum(universalYear(Y) + M);
  const universalDay = (D, M, Y) => reduceNum(universalYear(Y) + M + D);
  const personalYear = ({ d, m }, Y) => reduceNum(Y + m + d);
  const personalMonth = ({ d, m }, date) => {
    const PY = personalYear({ d, m }, date.getFullYear());
    const M = date.getMonth() + 1;
    return reduceNum(PY + M);
  };
  const personalDay = ({ d, m }, date) => {
    const PY = personalYear({ d, m }, date.getFullYear());
    const M = date.getMonth() + 1;
    const D = date.getDate();
    return reduceNum(PY + D + M);
  };

  // Solo mostrar emoji si es un día importante
  const getImportantEmoji = (num) => {
    return isImportantDay(num) ? getImportantDayInfo(num)?.emoji : null;
  };

  const matrix = buildMonthMatrix(currentDate);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-[#d4af37]">
      {/* Header con navegación */}
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
        {/* Botón mes anterior */}
        <button
          onClick={onPrevMonth}
          disabled={!canPrevMonth}
          className={`p-1.5 rounded-lg transition-all ${
            canPrevMonth
              ? 'text-morado active:bg-morado/10'
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <div className="text-morado font-semibold text-sm text-center flex-1">
          {currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </div>
        
        {/* Botón mes siguiente */}
        <button
          onClick={onNextMonth}
          disabled={!canNextMonth}
          className={`p-1.5 rounded-lg transition-all ${
            canNextMonth
              ? 'text-morado active:bg-morado/10'
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      {/* Días de la semana compactos */}
      <div className="grid grid-cols-7 text-center text-xs font-medium text-morado py-2 border-b border-gray-100">
        {['L','M','M','J','V','S','D'].map((d, index) => (
          <div key={index} className="px-1">{d}</div>
        ))}
      </div>
      
      {/* Grid de días optimizado para móvil */}
      <div className="grid grid-cols-7 gap-0.5 bg-gray-100">
        {matrix.map((cell, idx) => {
          const d = cell.date;
          const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const pd = personalDay(dob, d);
          const ud = universalDay(d.getDate(), d.getMonth() + 1, d.getFullYear());
          const isSelected = iso === selectedISO;
          const inMonth = cell.inMonth;
          
          // Detectar si es HOY
          const today = new Date();
          const isToday = d.getDate() === today.getDate() && 
                         d.getMonth() === today.getMonth() && 
                         d.getFullYear() === today.getFullYear();
          
          const isAllowed = isDateAllowed ? isDateAllowed(iso) : true;
          const isDisabled = !inMonth || !isAllowed;
          const emoji = getImportantEmoji(pd);
          
          return (
            <button 
              key={idx} 
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  setModalDay(d);
                  onPick(iso);
                }
              }} 
              className={`
                relative min-h-[4rem] p-2 text-center transition-all
                ${isDisabled 
                  ? 'bg-gray-200 opacity-40 cursor-not-allowed'
                  : isToday 
                    ? 'border-4 border-morado shadow-xl shadow-morado/20 bg-morado/5 ring-2 ring-morado/30' 
                    : `${isSelected ? 'bg-morado text-white shadow-lg' : ''} ${isImportantDay(pd) ? getImportantDayColor(pd) : 'bg-white'} hover:bg-gray-50`}
              `}
            >
              {/* Indicador HOY */}
              {isToday && !isDisabled && (
                <div className="absolute top-0 right-0 bg-morado text-white text-[0.5rem] font-bold px-1 py-0.5 rounded-bl-lg">
                  HOY
                </div>
              )}
              
              {/* Fecha */}
              <div className={`text-sm font-semibold ${isToday ? 'text-morado font-bold' : isSelected ? 'text-white' : 'text-gray-700'}`}>
                {d.getDate()}
              </div>
              
              {/* Binomio Personal/Universal */}
              <div className="flex items-center justify-center mt-1">
                <span className={`
                  text-xs font-bold rounded-full px-1.5 py-0.5
                  ${isSelected 
                    ? 'bg-white text-morado' 
                    : 'bg-morado/10 text-morado'
                  }
                `}>
                  {pd}/{ud}
                </span>
              </div>
              
              {/* Emoji solo si existe y es importante */}
              {emoji && (
                <div className="absolute top-1 right-1 pointer-events-none">
                  <span className="text-sm opacity-80">
                    {emoji}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Leyenda compacta para móvil */}
      <div className="px-3 py-2 bg-gray-50 rounded-b-xl text-center">
        <div className="text-xs text-gray-600">
          🌟3 💰8 💡11 🚀22
        </div>
        {rangeSummary && (
          <div className="mt-1 text-[11px] text-gray-500 leading-snug">
            {rangeSummary}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileCalendar;
